import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { PillTabsList, PillTabsTrigger } from "@/components/admin/pill-tabs";
import { AdminPageHeader } from "@/components/admin/crud-helpers";
import { MEGA_MENU_ICON_NAMES, resolveMegaMenuIcon } from "@/lib/mega-menu-icons";
import type { MegaMenuConfig, MegaMenuCategory, MegaMenuGroup, MegaMenuItem } from "@/hooks/use-mega-menu";
import { Plus, Trash2, ArrowUp, ArrowDown, Save, RotateCcw, Palette } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/navigation")({
  component: NavigationAdmin,
});

/** Top-level menus shown in the public header. Keep in sync with header.tsx. */
const MENU_DEFS: { key: string; label: string }[] = [
  { key: "services", label: "Services" },
  { key: "marketing", label: "Marketing" },
  { key: "software", label: "Software" },
  { key: "hosting", label: "Hosting" },
  { key: "company", label: "Company" },
];

function NavigationAdmin() {
  const [active, setActive] = useState(MENU_DEFS[0].key);

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Navigation menus"
        description="Manage every header mega menu — categories, groups, items, icons, and links. The public header reflects changes immediately after saving."
      />

      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span>Also looking for site logo, favicon, fonts, or colors?</span>
        <Button asChild size="sm" variant="outline" className="h-7">
          <Link to="/admin/appearance">
            <Palette className="h-3.5 w-3.5" /> Open Appearance
          </Link>
        </Button>
      </div>

      <Tabs value={active} onValueChange={setActive}>
        <PillTabsList search className="flex flex-wrap h-auto">
          {MENU_DEFS.map((m) => (
            <PillTabsTrigger key={m.key} value={m.key}>{m.label}</PillTabsTrigger>
          ))}
        </PillTabsList>

        {MENU_DEFS.map((m) => (
          <TabsContent key={m.key} value={m.key} className="mt-6">
            {active === m.key && <MenuEditor menuKey={m.key} fallbackLabel={m.label} />}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function MenuEditor({ menuKey, fallbackLabel }: { menuKey: string; fallbackLabel: string }) {
  const EMPTY: MegaMenuConfig = { label: fallbackLabel, categories: [] };
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["mega-menu", menuKey, "admin"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("mega_menus")
        .select("config")
        .eq("key", menuKey)
        .maybeSingle();
      if (error) throw error;
      return (data?.config as MegaMenuConfig) ?? EMPTY;
    },
  });

  const [draft, setDraft] = useState<MegaMenuConfig>(EMPTY);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (data) {
      setDraft(JSON.parse(JSON.stringify(data)));
      setDirty(false);
    }
  }, [data]);

  const save = useMutation({
    mutationFn: async (cfg: MegaMenuConfig) => {
      const { error } = await supabase
        .from("mega_menus")
        .upsert({ key: menuKey, config: cfg as any, updated_at: new Date().toISOString() });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(`${fallbackLabel} menu saved`);
      setDirty(false);
      qc.invalidateQueries({ queryKey: ["mega-menu"] });
    },
    onError: (e: any) => toast.error(e.message ?? "Save failed"),
  });

  function update(next: MegaMenuConfig) {
    setDraft(next);
    setDirty(true);
  }

  if (isLoading) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-border bg-card p-4 space-y-4">
        <div className="grid gap-3 sm:grid-cols-[1fr_2fr]">
          <div className="grid gap-2">
            <Label>Menu label</Label>
            <Input
              value={draft.label ?? ""}
              onChange={(e) => update({ ...draft, label: e.target.value })}
              placeholder={fallbackLabel}
            />
          </div>
          <FeaturedEditor
            featured={draft.featured}
            onChange={(f) => update({ ...draft, featured: f })}
          />
        </div>
      </div>

      <div className="flex items-center justify-between gap-2">
        <Button
          variant="outline"
          onClick={() =>
            update({
              ...draft,
              categories: [
                ...draft.categories,
                { label: "New category", desc: "", icon: "Sparkles", groups: [{ title: "Group", items: [] }] },
              ],
            })
          }
        >
          <Plus className="h-4 w-4" /> Add category
        </Button>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            disabled={!dirty || !data}
            onClick={() => data && setDraft(JSON.parse(JSON.stringify(data)))}
          >
            <RotateCcw className="h-4 w-4" /> Revert
          </Button>
          <Button onClick={() => save.mutate(draft)} disabled={!dirty || save.isPending}>
            <Save className="h-4 w-4" /> {save.isPending ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>

      <Accordion type="multiple" className="space-y-2">
        {draft.categories.map((cat, ci) => (
          <AccordionItem
            key={ci}
            value={`cat-${ci}`}
            className="rounded-lg border border-border bg-card"
          >
            <AccordionTrigger className="px-4 py-3 hover:no-underline">
              <CategoryHeader cat={cat} index={ci} total={draft.categories.length} />
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <CategoryEditor
                cat={cat}
                onChange={(next) => {
                  const cats = [...draft.categories];
                  cats[ci] = next;
                  update({ ...draft, categories: cats });
                }}
                onMove={(dir) => {
                  const cats = [...draft.categories];
                  const j = ci + dir;
                  if (j < 0 || j >= cats.length) return;
                  [cats[ci], cats[j]] = [cats[j], cats[ci]];
                  update({ ...draft, categories: cats });
                }}
                onDelete={() => {
                  if (!confirm(`Delete category "${cat.label}"?`)) return;
                  const cats = draft.categories.filter((_, k) => k !== ci);
                  update({ ...draft, categories: cats });
                }}
              />
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}

function CategoryHeader({ cat, index, total }: { cat: MegaMenuCategory; index: number; total: number }) {
  const Icon = resolveMegaMenuIcon(cat.icon);
  const itemCount = (cat.groups ?? []).reduce((n, g) => n + (g.items?.length ?? 0), 0);
  return (
    <div className="flex w-full items-center gap-3 text-left">
      <Icon className="h-4 w-4 text-primary" />
      <span className="font-medium">{cat.label || "(untitled)"}</span>
      <span className="text-xs text-muted-foreground">
        · {cat.groups?.length ?? 0} groups · {itemCount} items
      </span>
      <span className="ml-auto text-xs text-muted-foreground">
        {index + 1} / {total}
      </span>
    </div>
  );
}

function FeaturedEditor({
  featured,
  onChange,
}: {
  featured?: MegaMenuConfig["featured"];
  onChange: (f: MegaMenuConfig["featured"]) => void;
}) {
  const f = featured ?? { title: "", desc: "", to: "" };
  return (
    <div className="grid gap-2">
      <Label>Featured panel (optional)</Label>
      <div className="grid gap-2 sm:grid-cols-3">
        <Input placeholder="Title" value={f.title} onChange={(e) => onChange({ ...f, title: e.target.value })} />
        <Input placeholder="Description" value={f.desc} onChange={(e) => onChange({ ...f, desc: e.target.value })} />
        <Input placeholder="/contact" value={f.to} onChange={(e) => onChange({ ...f, to: e.target.value })} />
      </div>
    </div>
  );
}

function CategoryEditor({
  cat,
  onChange,
  onMove,
  onDelete,
}: {
  cat: MegaMenuCategory;
  onChange: (next: MegaMenuCategory) => void;
  onMove: (dir: -1 | 1) => void;
  onDelete: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
        <div className="grid gap-1.5">
          <Label className="text-xs">Label</Label>
          <Input value={cat.label} onChange={(e) => onChange({ ...cat, label: e.target.value })} />
        </div>
        <div className="grid gap-1.5">
          <Label className="text-xs">Icon</Label>
          <IconSelect value={cat.icon} onChange={(v) => onChange({ ...cat, icon: v })} />
        </div>
        <div className="flex items-end gap-1">
          <Button size="sm" variant="ghost" onClick={() => onMove(-1)} title="Move up">
            <ArrowUp className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onMove(1)} title="Move down">
            <ArrowDown className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={onDelete} title="Delete">
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </div>
      <div className="grid gap-1.5">
        <Label className="text-xs">Description</Label>
        <Textarea
          rows={2}
          value={cat.desc}
          onChange={(e) => onChange({ ...cat, desc: e.target.value })}
        />
      </div>

      <div className="space-y-3">
        {(cat.groups ?? []).map((g, gi) => (
          <GroupEditor
            key={gi}
            group={g}
            onChange={(ng) => {
              const groups = [...cat.groups];
              groups[gi] = ng;
              onChange({ ...cat, groups });
            }}
            onMove={(dir) => {
              const groups = [...cat.groups];
              const j = gi + dir;
              if (j < 0 || j >= groups.length) return;
              [groups[gi], groups[j]] = [groups[j], groups[gi]];
              onChange({ ...cat, groups });
            }}
            onDelete={() => {
              if (!confirm(`Delete group "${g.title}"?`)) return;
              onChange({ ...cat, groups: cat.groups.filter((_, k) => k !== gi) });
            }}
          />
        ))}
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            onChange({
              ...cat,
              groups: [...(cat.groups ?? []), { title: "New group", items: [] }],
            })
          }
        >
          <Plus className="h-4 w-4" /> Add group
        </Button>
      </div>
    </div>
  );
}

function GroupEditor({
  group,
  onChange,
  onMove,
  onDelete,
}: {
  group: MegaMenuGroup;
  onChange: (g: MegaMenuGroup) => void;
  onMove: (dir: -1 | 1) => void;
  onDelete: () => void;
}) {
  return (
    <div className="rounded-md border border-border/60 bg-background/40 p-3 space-y-3">
      <div className="flex items-center gap-2">
        <Input
          className="max-w-xs"
          value={group.title}
          onChange={(e) => onChange({ ...group, title: e.target.value })}
          placeholder="Group title"
        />
        <div className="ml-auto flex gap-1">
          <Button size="sm" variant="ghost" onClick={() => onMove(-1)}>
            <ArrowUp className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onMove(1)}>
            <ArrowDown className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={onDelete}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        {(group.items ?? []).map((it, ii) => (
          <ItemRow
            key={ii}
            item={it}
            onChange={(ni) => {
              const items = [...group.items];
              items[ii] = ni;
              onChange({ ...group, items });
            }}
            onMove={(dir) => {
              const items = [...group.items];
              const j = ii + dir;
              if (j < 0 || j >= items.length) return;
              [items[ii], items[j]] = [items[j], items[ii]];
              onChange({ ...group, items });
            }}
            onDelete={() => onChange({ ...group, items: group.items.filter((_, k) => k !== ii) })}
          />
        ))}
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            onChange({
              ...group,
              items: [...(group.items ?? []), { label: "New item", to: "/services", icon: "Sparkles" }],
            })
          }
        >
          <Plus className="h-4 w-4" /> Add item
        </Button>
      </div>
    </div>
  );
}

function ItemRow({
  item,
  onChange,
  onMove,
  onDelete,
}: {
  item: MegaMenuItem;
  onChange: (it: MegaMenuItem) => void;
  onMove: (dir: -1 | 1) => void;
  onDelete: () => void;
}) {
  return (
    <div className="rounded-md border border-border/50 bg-card/40 p-2 space-y-2">
      <div className="grid gap-2 sm:grid-cols-[1fr_1.4fr_180px_auto] items-center">
        <Input
          value={item.label}
          onChange={(e) => onChange({ ...item, label: e.target.value })}
          placeholder="Label"
        />
        <Input
          value={item.to}
          onChange={(e) => onChange({ ...item, to: e.target.value })}
          placeholder="/services/sub-page"
        />
        <IconSelect value={item.icon} onChange={(v) => onChange({ ...item, icon: v })} />
        <div className="flex">
          <Button size="sm" variant="ghost" onClick={() => onMove(-1)}>
            <ArrowUp className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onMove(1)}>
            <ArrowDown className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={onDelete}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </div>
      <Input
        value={item.desc ?? ""}
        onChange={(e) => onChange({ ...item, desc: e.target.value })}
        placeholder="Short description (optional)"
        className="text-xs"
      />
    </div>
  );
}

function IconSelect({ value, onChange }: { value?: string; onChange: (v: string) => void }) {
  const Icon = resolveMegaMenuIcon(value);
  return (
    <Select value={value ?? "Sparkles"} onValueChange={onChange}>
      <SelectTrigger>
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-primary" />
          <SelectValue placeholder="Pick icon" />
        </div>
      </SelectTrigger>
      <SelectContent className="max-h-72">
        {MEGA_MENU_ICON_NAMES.map((name) => {
          const I = resolveMegaMenuIcon(name);
          return (
            <SelectItem key={name} value={name}>
              <span className="inline-flex items-center gap-2">
                <I className="h-4 w-4" /> {name}
              </span>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}
